<script lang="ts" setup>
import DeleteIcon from "@/components/icon/deleteIcon.vue";
import {
  createPrivateSession,
  startChatSession,
} from "@/components/mobile/chat/chatUtils";
import MateChatListItem from "@/components/mobile/chat/MateChatListItem.vue";
import i18n from "@/locales";
import { useChatSession } from "@/stores/chatSession";
import { Mate } from "@/types/entity";
import { showConfirmDialog, showLoadingToast, SwipeCell } from "vant";
import { computed, PropType } from "vue";
import { useRouter } from "vue-router";

const props = defineProps({
  mate: {
    type: Object as PropType<Mate>,
    required: true,
  },
  myMateId: {
    type: Number,
  },
});

const chatSessionStore = useChatSession();
const router = useRouter();
const privateSessionList = computed(() => {
  if (!props.mate) return [];
  return chatSessionStore.getPrivateSessionListByMateId(props.mate.id);
});
// Event Handler
const onClick = (sessionId: number) => {
  chatSessionStore.goChat(router, props.mate.uuid, sessionId);
};

const onDeletePrivateSession = async (sessionId: number) => {
  await showConfirmDialog({
    title: i18n.global.t("ConfirmModal.deletePrivateChatTitle"),
    message: i18n.global.t("ConfirmModal.deletePrivateChatInfo"),
    confirmButtonText: i18n.global.t("Buttons.confirm"),
    cancelButtonText: i18n.global.t("Buttons.cancel"),
  });
  const loading = showLoadingToast({
    message: i18n.global.t("Message.deletingPrivateChat"),
  });
  try {
    await chatSessionStore.deletePrivateSession(props.mate.id, sessionId);
  } finally {
    loading.close();
  }
};

const clickCreatePrivateChat = (mateId: number, mateUuid: string) => {
  createPrivateSession(
    mateId,
    mateUuid,
    "push",
    undefined,
    undefined,
    props.myMateId
  );
};
</script>
<template>
  <div class="w-full flex flex-col gap-2 overflow-hidden py-2">
    <div class="flex justify-between items-center px-2">
      <span class="text-base-content/50 text-base font-bold">{{
        $t("Buttons.recentChat")
      }}</span>
      <button
        class="btn btn-xs btn-primary"
        @click="clickCreatePrivateChat(mate.id, mate.uuid)"
      >
        <i class="iconify ic--baseline-add" />
        {{ $t("Buttons.createPrivateChat") }}
      </button>
    </div>
    <div class="h-full">
      <div
        v-if="privateSessionList.length === 0"
        class="flex flex-col items-center border text-base-content/50 p-4 rounded gap-2 border-dashed mx-2"
      >
        {{ $t("Chats.noInitiatedPrivateChat") }}
      </div>
      <div v-else>
        <swipe-cell
          v-for="item in privateSessionList"
          :key="item.id"
          class="border-b"
        >
          <mate-chat-list-item
            :session-id="item.id"
            :avatar-url="item.avatarUrl"
            :message="item.content"
            :name="item.title"
            :time="item.time"
            :type="2"
            :unread="0"
            class="border-b-0"
            @click="onClick(item.id)"
          />
          <template #right>
            <div class="h-full w-10 flex items-center justify-center">
              <button
                class="btn btn-rounded btn-neutral btn-xs rounded"
                @click="onDeletePrivateSession(item.id)"
              >
                <delete-icon class="icon-xs" />
              </button>
            </div>
          </template>
        </swipe-cell>
      </div>
    </div>
  </div>
</template>
